import type {
  FairUseConfig,
  FairUseConfigUpdate,
  FairUseEntitlementSchema,
} from "@/api/admin/fair-use"

import {
  fairUseEntitlementIssues,
  fairUseFormStateToEntitlements,
  fairUseFormStateToVoicePrices,
  fairUseVoicePricesIssue,
} from "./fair-use-entitlements-utils"
import {
  fairUseFormStateToImagePrices,
  fairUseFormStateToVoiceTokenPrices,
  fairUseImagePricesIssue,
  fairUseVoiceTokenPricesIssue,
} from "./fair-use-pricing-utils"
import {
  type FairUseConfigFormState,
  fairUseConfigToFormState,
  parseNonNegativeNumber,
  parsePositiveNumber,
} from "./fair-use-utils"

// Non-component helpers for the fair-use SETTINGS surface (Configuration →
// Fair use). Kept out of the .tsx files so those export components only
// (react-refresh), and kept out of `fair-use-utils.ts`, which is already at
// the file-size limit.
//
// The split that matters here is the one the backend enforces: the Limits
// panel writes the DELEGABLE fields, the Plans & pricing panel writes the
// PLATFORM-ONLY commercial envelope, and neither ever writes `overrides`.
// Each panel therefore parses and validates only its own fields — a bad
// number in Plans must not be able to disable the Limits Save, which is
// exactly what a single whole-form parse (`fairUseFormStateToConfig`) would
// do now that the two live on separate tabs behind separate buttons.

/**
 * The four steps of the ladder, weakest first, in the vocabulary the admin
 * reads them in. The ids are UI-only; each one maps to the backend's own
 * `*_percent` field through {@link FAIR_USE_THRESHOLD_CONFIG_FIELDS}.
 */
export type FairUseThresholdId = "warn" | "request" | "fallback" | "stop"

export const FAIR_USE_THRESHOLD_IDS: readonly FairUseThresholdId[] = [
  "warn",
  "request",
  "fallback",
  "stop",
]

/** Which form field each step edits. */
const FAIR_USE_THRESHOLD_FORM_FIELDS: Record<
  FairUseThresholdId,
  "warnPercent" | "requestCtaPercent" | "downgradePercent" | "blockPercent"
> = {
  warn: "warnPercent",
  request: "requestCtaPercent",
  fallback: "downgradePercent",
  stop: "blockPercent",
}

/** Which wire field each step writes. Kept next to the form mapping on purpose. */
const FAIR_USE_THRESHOLD_CONFIG_FIELDS: Record<
  FairUseThresholdId,
  "warn_percent" | "request_cta_percent" | "downgrade_percent" | "block_percent"
> = {
  warn: "warn_percent",
  request: "request_cta_percent",
  fallback: "downgrade_percent",
  stop: "block_percent",
}

/** The DOM id of each step's input, so the ladder and its tests agree on one name. */
export const FAIR_USE_THRESHOLD_INPUT_IDS: Record<FairUseThresholdId, string> = {
  warn: "fair-use-warn-percent",
  request: "fair-use-request-percent",
  fallback: "fair-use-fallback-percent",
  stop: "fair-use-stop-percent",
}

/**
 * The accepted span for a step, in percent of the user's monthly budget.
 *
 * The floor is 1 rather than 0 because a step at 0 % fires before the user has
 * spent anything — a "you are near your budget" notice on an untouched month.
 * The ceiling is 500 because the ladder is drawn to scale: a stray "10000"
 * would squash the other three markers onto the left edge and read as though
 * they were all equal.
 */
export const FAIR_USE_MIN_THRESHOLD_PERCENT = 1
export const FAIR_USE_MAX_THRESHOLD_PERCENT = 500

export type FairUseThresholdValues = Record<FairUseThresholdId, string>

/** The four editable step strings, pulled out of the shared form state. */
export function readFairUseThresholdValues(form: FairUseConfigFormState): FairUseThresholdValues {
  return {
    warn: form[FAIR_USE_THRESHOLD_FORM_FIELDS.warn],
    request: form[FAIR_USE_THRESHOLD_FORM_FIELDS.request],
    fallback: form[FAIR_USE_THRESHOLD_FORM_FIELDS.fallback],
    stop: form[FAIR_USE_THRESHOLD_FORM_FIELDS.stop],
  }
}

/** The form field one step writes, for the panel's onChange. */
export function fairUseThresholdFormField(
  id: FairUseThresholdId
): keyof FairUseConfigFormState & string {
  return FAIR_USE_THRESHOLD_FORM_FIELDS[id]
}

/**
 * One step as a number, or null when it is unusable.
 *
 * Decimal parsing (comma or point, no thousands grouping, no `Number`
 * coercions) is `parsePositiveNumber`'s job and is shared with every other
 * numeric field on this surface; the only thing added here is the range.
 */
export function parseFairUseThresholdPercent(value: string): number | null {
  const parsed = parsePositiveNumber(value)
  if (parsed == null) return null
  if (parsed < FAIR_USE_MIN_THRESHOLD_PERCENT || parsed > FAIR_USE_MAX_THRESHOLD_PERCENT) {
    return null
  }
  return parsed
}

/**
 * What is wrong with the ladder, if anything — `"range"` when any step is not
 * a usable percentage, `"order"` when the four do not strictly increase.
 *
 * STRICTLY increasing, not merely non-decreasing: two steps on the same
 * percentage means one of them can never be observed. Warn and "Offer Request
 * more" both at 90 % looks configured but only ever produces the later of the
 * two, so the admin's intent — a notice first, then the offer — silently does
 * not happen.
 */
export type FairUseThresholdIssue = "range" | "order" | null

export function fairUseThresholdIssue(values: FairUseThresholdValues): FairUseThresholdIssue {
  const parsed = FAIR_USE_THRESHOLD_IDS.map((id) => parseFairUseThresholdPercent(values[id]))
  if (parsed.some((percent) => percent == null)) return "range"
  for (let index = 1; index < parsed.length; index += 1) {
    if ((parsed[index] as number) <= (parsed[index - 1] as number)) return "order"
  }
  return null
}

export type FairUseThresholdMarker = {
  id: FairUseThresholdId
  percent: number
  /** Position along the rail, 0–100, as a percentage of the rail's own width. */
  left: number
}

/**
 * Where each step sits on a rail that runs from 0 to the largest step.
 *
 * Markers are produced whenever every step PARSES, even when the order is
 * wrong: seeing "Stop" sitting to the left of "Warn" is the clearest possible
 * statement of what the inline order error is talking about, and hiding the
 * rail at that moment would leave only the sentence.
 *
 * Returns `[]` when any step is unusable — there is no honest scale to draw.
 */
export function fairUseThresholdMarkers(values: FairUseThresholdValues): FairUseThresholdMarker[] {
  const parsed = FAIR_USE_THRESHOLD_IDS.map((id) => parseFairUseThresholdPercent(values[id]))
  if (parsed.some((percent) => percent == null)) return []
  const percents = parsed as number[]
  const max = Math.max(...percents)
  return FAIR_USE_THRESHOLD_IDS.map((id, index) => ({
    id,
    percent: percents[index],
    left: (percents[index] / max) * 100,
  }))
}

/**
 * The maximum SEK one lead may be allowed to approve per month.
 *
 * Mirrors `fair_use/admin.py::MAX_CONFIG_SEK`, the ceiling every SEK field on
 * this config shares — it exists so a delegated admin cannot set a lead
 * allowance so large it is effectively unbounded.
 */
export const FAIR_USE_MAX_LEAD_ALLOWANCE_SEK = 1_000_000

/** The lead allowance as a number, or null when it is unusable (0 is legal). */
export function parseFairUseLeadAllowance(value: string): number | null {
  const parsed = parseNonNegativeNumber(value)
  if (parsed == null || parsed > FAIR_USE_MAX_LEAD_ALLOWANCE_SEK) return null
  return parsed
}

/** Whether a config carries the delegable round-2 lead allowance at all. */
export function fairUseHasLeadAllowance(
  config: Pick<FairUseConfig, "lead_allowance_sek_per_month">
): boolean {
  return typeof config.lead_allowance_sek_per_month === "number"
}

export type FairUseLimitsUpdateOptions = {
  /**
   * Send `lead_allowance_sek_per_month`. False on a round-1 backend, where the
   * field does not exist and would answer 400 `invalid_config` — taking the
   * four thresholds down with it.
   */
  includeLeadAllowance?: boolean
}

/**
 * What the Limits panel's Save puts on the wire, or null when the panel is not
 * in a saveable state.
 *
 * Exactly the delegable half of the config: enforcement, the four steps, the
 * fallback model, the reasoning effort that goes with it and — round 2 — the
 * lead approval allowance. A commercial field here, even an unchanged one,
 * answers 403 `platform_admin_required` and applies nothing, so a delegated
 * admin could never save a step at all.
 */
export function fairUseLimitsUpdate(
  form: FairUseConfigFormState,
  options: FairUseLimitsUpdateOptions = {}
): FairUseConfigUpdate | null {
  const values = readFairUseThresholdValues(form)
  if (fairUseThresholdIssue(values) !== null) return null
  const downgradeModel = form.downgradeModel.trim()
  if (!downgradeModel) return null
  let leadAllowance: number | null = null
  if (options.includeLeadAllowance) {
    leadAllowance = parseFairUseLeadAllowance(form.leadAllowanceSek)
    if (leadAllowance == null) return null
  }

  const updates: FairUseConfigUpdate = {
    enabled: form.enabled,
    downgrade_model: downgradeModel,
    // The select can only hold a valid stop, so there is nothing to reject
    // here — it is normalized on the way IN (`fairUseConfigToFormState`).
    downgrade_reasoning_effort: form.downgradeReasoningEffort,
  }
  for (const id of FAIR_USE_THRESHOLD_IDS) {
    updates[FAIR_USE_THRESHOLD_CONFIG_FIELDS[id]] = parseFairUseThresholdPercent(
      values[id]
    ) as number
  }
  if (leadAllowance != null) updates.lead_allowance_sek_per_month = leadAllowance
  return updates
}

/**
 * What the lead-allowance Save puts on the wire, or null when the amount is
 * not a usable number.
 *
 * ONE field, deliberately. The allowance sits on the fair-use config document
 * but has a Save of its own, on a different tab from the four thresholds — so
 * sending the whole delegable half (as {@link fairUseLimitsUpdate} does for
 * the panel that renders all of it) would persist whatever the admin had
 * half-typed on Settings and walked away from, and clear neither that panel's
 * dirty flag nor its armed Save. The route merges field by field, so a
 * single-field payload is a complete update.
 */
export function fairUseLeadAllowanceUpdate(
  form: FairUseConfigFormState
): FairUseConfigUpdate | null {
  const leadAllowance = parseFairUseLeadAllowance(form.leadAllowanceSek)
  if (leadAllowance == null) return null
  return { lead_allowance_sek_per_month: leadAllowance }
}

/**
 * What is wrong with the plan amounts, if anything — `"range"` when an amount
 * is not a usable number, `"order"` when the three monthly budgets do not rise.
 */
export type FairUsePlansIssue = "range" | "order" | null

type ParsedFairUsePlans = {
  default_monthly_cap_sek: number
  pro_monthly_cap_sek: number
  unlimited_monthly_cap_sek: number
  block_cap_sek: number
  block_list_price_sek: number
}

function parseFairUsePlans(form: FairUseConfigFormState): ParsedFairUsePlans | null {
  const standard = parsePositiveNumber(form.defaultMonthlyCapSek)
  const pro = parsePositiveNumber(form.proMonthlyCapSek)
  const unlimited = parsePositiveNumber(form.unlimitedMonthlyCapSek)
  const topUpSize = parsePositiveNumber(form.blockCapSek)
  // The only amount the backend lets be 0 — a free or bundled top-up.
  const topUpPrice = parseNonNegativeNumber(form.blockListPriceSek)
  if (
    standard == null ||
    pro == null ||
    unlimited == null ||
    topUpSize == null ||
    topUpPrice == null
  ) {
    return null
  }
  return {
    default_monthly_cap_sek: standard,
    pro_monthly_cap_sek: pro,
    unlimited_monthly_cap_sek: unlimited,
    block_cap_sek: topUpSize,
    block_list_price_sek: topUpPrice,
  }
}

export function fairUsePlansIssue(form: FairUseConfigFormState): FairUsePlansIssue {
  const parsed = parseFairUsePlans(form)
  if (!parsed) return "range"
  if (
    parsed.default_monthly_cap_sek > parsed.pro_monthly_cap_sek ||
    parsed.pro_monthly_cap_sek > parsed.unlimited_monthly_cap_sek
  ) {
    return "order"
  }
  return null
}

export type FairUsePlansUpdateOptions = {
  /**
   * Send the round-2 `entitlements` matrix and `voice_price_sek_per_minute`.
   *
   * False on a round-1 backend, which knows neither field: an unknown key
   * answers 400 `invalid_config` and applies NOTHING, so including them
   * blindly would take the five plan amounts down with them.
   */
  includeEntitlements?: boolean
  /**
   * Send the round-3 `image_price_sek` table. Gated SEPARATELY from the
   * matrix, and from the token table below, because each field is its own
   * `_VALID_CONFIG_FIELDS` entry: a backend that knows one may predate the
   * other, and one unknown key takes the whole payload down with it.
   */
  includeImagePrices?: boolean
  /** Send the round-3 `voice_token_prices` table. Same gating rule. */
  includeVoiceTokenPrices?: boolean
  /** Bounds and the effort ladder for the matrix, when the platform sent them. */
  schema?: FairUseEntitlementSchema
}

/**
 * What the Plans & pricing Save puts on the wire, or null when an amount is
 * unusable, the three monthly budgets do not rise, or a matrix cell or voice
 * rate cannot be read.
 *
 * The commercial envelope and nothing else. `overrides` — the per-user plan
 * map — is deliberately absent: the config PUT applies what it is given onto
 * the LATEST stored document, so posting this browser's snapshot of the map
 * would drop any plan assigned since the form was loaded. Plans are written
 * one oid at a time through `PUT /api/admin/fair-use/users/<oid>/tier`.
 *
 * `entitlements`, `voice_price_sek_per_minute`, `image_price_sek` and
 * `voice_token_prices` are platform-only for the
 * same reason the amounts are: the matrix decides what every plan may DO, so a
 * delegated admin could move their whole tenant onto the Unlimited feature row
 * without touching a single SEK figure.
 */
export function fairUsePlansUpdate(
  form: FairUseConfigFormState,
  options: FairUsePlansUpdateOptions = {}
): FairUseConfigUpdate | null {
  if (fairUsePlansIssue(form) !== null) return null
  const parsed = parseFairUsePlans(form)
  if (!parsed) return null
  const updates: FairUseConfigUpdate = { ...parsed }

  if (options.includeEntitlements) {
    const entitlements = fairUseFormStateToEntitlements(form.entitlements, options.schema)
    const voicePrices = fairUseFormStateToVoicePrices(form.voicePrices)
    if (!entitlements || !voicePrices) return null
    updates.entitlements = entitlements
    updates.voice_price_sek_per_minute = voicePrices
  }
  if (options.includeImagePrices) {
    const imagePrices = fairUseFormStateToImagePrices(form.imagePrices)
    if (!imagePrices) return null
    updates.image_price_sek = imagePrices
  }
  if (options.includeVoiceTokenPrices) {
    const voiceTokenPrices = fairUseFormStateToVoiceTokenPrices(form.voiceTokenPrices)
    if (!voiceTokenPrices) return null
    updates.voice_token_prices = voiceTokenPrices
  }
  return updates
}

/**
 * Whether the Plans & pricing tab is in a saveable state — the amounts, the
 * matrix and the voice rates together.
 *
 * One answer for one Save button: the three sections write one config
 * document, and a bad matrix cell must disable the button that would otherwise
 * send the amounts alone and report them as "everything saved".
 */
export function fairUsePlansPanelIsSaveable(
  form: FairUseConfigFormState,
  options: FairUsePlansUpdateOptions = {}
): boolean {
  if (fairUsePlansIssue(form) !== null) return false
  if (options.includeEntitlements) {
    if (fairUseEntitlementIssues(form.entitlements, options.schema).length > 0) return false
    if (fairUseVoicePricesIssue(form.voicePrices) !== null) return false
  }
  if (options.includeImagePrices && fairUseImagePricesIssue(form.imagePrices) !== null) return false
  if (options.includeVoiceTokenPrices && fairUseVoiceTokenPricesIssue(form.voiceTokenPrices)) {
    return false
  }
  return true
}

/** The five amounts the read-only tenant summary quotes, straight off the config. */
export function readFairUsePlanAmounts(config: FairUseConfig): ParsedFairUsePlans {
  return {
    default_monthly_cap_sek: config.default_monthly_cap_sek,
    pro_monthly_cap_sek: config.pro_monthly_cap_sek,
    unlimited_monthly_cap_sek: config.unlimited_monthly_cap_sek,
    block_cap_sek: config.block_cap_sek,
    block_list_price_sek: config.block_list_price_sek,
  }
}

/**
 * A SEK amount for reading, not for editing — grouped in the admin's own
 * locale ("2 000", "2,000") and never showing more precision than was stored.
 *
 * Only ever used on the read-only summary. The editable fields keep the raw
 * string the admin typed, because grouping it would immediately collide with
 * the decimal-separator rule the parser enforces.
 */
export function formatFairUseSek(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
}

/** The two panels that write the config document, for per-panel form patches. */
export type FairUseSettingsWriter = "limits" | "plans"

/**
 * Every Save that writes the config document — the two writers plus the lead
 * allowance, which rides on the delegable half but has a Save of its own on
 * another tab.
 */
export type FairUseSavedPanel = FairUseSettingsWriter | "leadAllowance"

/**
 * Overlay the post-write config onto only the panel that just saved.
 *
 * Tabs unmount, so the parent form is the only copy of unsaved edits. A full
 * `fairUseConfigToFormState` after a Limits save would discard typed Plans
 * fields (and vice versa). The saved panel still shows the platform's answer.
 */
export function applyFairUseSavedPanel(
  form: FairUseConfigFormState,
  config: FairUseConfig,
  panel: FairUseSavedPanel
): FairUseConfigFormState {
  const saved = fairUseConfigToFormState(config)
  if (panel === "leadAllowance") {
    // The allowance Save sends ONE field, so it may only read ONE field back.
    // Overlaying the whole delegable half here would overwrite a threshold
    // the admin is still typing on the Settings tab with the stored value.
    return { ...form, leadAllowanceSek: saved.leadAllowanceSek }
  }
  if (panel === "limits") {
    return {
      ...form,
      enabled: saved.enabled,
      warnPercent: saved.warnPercent,
      requestCtaPercent: saved.requestCtaPercent,
      downgradePercent: saved.downgradePercent,
      blockPercent: saved.blockPercent,
      downgradeModel: saved.downgradeModel,
      downgradeReasoningEffort: saved.downgradeReasoningEffort,
    }
  }
  return {
    ...form,
    defaultMonthlyCapSek: saved.defaultMonthlyCapSek,
    proMonthlyCapSek: saved.proMonthlyCapSek,
    unlimitedMonthlyCapSek: saved.unlimitedMonthlyCapSek,
    blockCapSek: saved.blockCapSek,
    blockListPriceSek: saved.blockListPriceSek,
  }
}
